import { appConfig } from '@/config/app.config';
import { getLogger } from '@/lib/logger';
import type { ApiError, ApiResponse, HttpClientConfig } from '@/types';
import { getTrackingService, type TrackingService } from '@/lib/tracking';

const logger = getLogger({ scope: 'lib.http-client' });

export interface BackendErrorResponse {
  error?: string;
  message?: string;
  status?: number;
  errorCode?: string;
  path?: string;
  timestamp?: string;
}

function isBackendErrorResponse(data: unknown): data is BackendErrorResponse {
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }
  const candidate = data as Record<string, unknown>;
  return (
    typeof candidate.message === 'string' ||
    typeof candidate.error === 'string' ||
    typeof candidate.errorCode === 'string' ||
    typeof candidate.path === 'string' ||
    typeof candidate.timestamp === 'string' ||
    typeof candidate.status === 'number'
  );
}

/**
 * Read the XSRF-TOKEN cookie value for CSRF protection
 */
function getCsrfToken(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * HTTP Client configuration and setup
 */
const DEFAULT_CONFIG: HttpClientConfig = {
  baseURL: appConfig.api.baseUrl,
  timeout: appConfig.auth.requestTimeoutMs,
  headers: { 'Content-Type': 'application/json' },
};

/**
 * Request configuration interface for fetch-based client
 */
interface RequestConfig {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  data?: unknown;
  headers?: Record<string, string>;
  timeout?: number;
  skipAuthRetry?: boolean;
}

/**
 * HTTP Client class using native Fetch API
 */
class HttpClient {
  private config: HttpClientConfig;
  private trackingService: TrackingService;

  constructor(config: Partial<HttpClientConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.trackingService = getTrackingService();
  }

  private addTrackingHeaders(): Record<string, string> {
    const sessionId = this.trackingService.getSessionId();
    if (!sessionId) {
      return {};
    }

    const headerName = this.trackingService.getSessionIdHeader();
    if (!headerName) {
      return {};
    }

    return { [headerName]: sessionId };
  }

  /**
   * Make HTTP request using fetch with automatic cookie credentials and error handling
   */
  async request<T = unknown>(requestConfig: RequestConfig): Promise<{ data: ApiResponse<T> }> {
    const {
      url,
      method = 'GET',
      data,
      headers = {},
      timeout = this.config.timeout,
    } = requestConfig;

    // Build full URL
    const fullUrl = url.startsWith('http') ? url : `${this.config.baseURL}${url}`;

    const trackingHeaders = this.addTrackingHeaders();
    // Prepare headers
    const requestHeaders: Record<string, string> = {
      ...this.config.headers,
      ...headers,
      ...trackingHeaders,
    };

    // Add CSRF token for state-changing requests
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        requestHeaders['X-XSRF-TOKEN'] = csrfToken;
      }
    }

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // Make the request with credentials: 'include' for cookie-based auth
      const response = await fetch(fullUrl, {
        method,
        headers: requestHeaders,
        body: data ? JSON.stringify(data) : undefined,
        signal: controller.signal,
        credentials: 'include',
      });

      clearTimeout(timeoutId);

      // Handle 401 errors with token refresh — only if the user has an active session
      if (response.status === 401 && !requestConfig.skipAuthRetry) {
        try {
          // Import here to avoid circular dependency
          const { useAuthStore } = await import('@/stores/auth-store');
          const { tokenRefreshManager } =
            await import('@features/auth/services/token-refresh-manager');

          // Only attempt refresh if the store believes we have an authenticated session.
          // If the user is not authenticated there is nothing to refresh and we should
          // not trigger another refresh cycle.
          const isAuthenticated = useAuthStore.getState().isAuthenticated;

          if (isAuthenticated) {
            const refreshSuccess = await tokenRefreshManager.forceRefresh();

            if (refreshSuccess) {
              // Retry the original request with the new cookie
              return this.request({ ...requestConfig, skipAuthRetry: true });
            }
          }
        } catch (refreshError) {
          logger.error('Token refresh failed during request retry', { error: refreshError });
          // Fall through to handle the 401 error
        }
      }

      // Parse response
      let responseData: unknown;
      const contentType = response.headers.get('content-type');

      if (
        contentType &&
        (contentType.includes('application/json') ||
          contentType.includes('application/problem+json'))
      ) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }
      // Handle non-2xx responses
      if (!response.ok) {
        throw createApiErrorFromResponse(responseData, response);
      }

      // Transform successful responses to our ApiResponse format
      // If the backend already returns an ApiResponse format, use it directly
      if (responseData && typeof responseData === 'object' && 'success' in responseData) {
        return { data: responseData as ApiResponse<T> };
      }

      // Otherwise, wrap it in our ApiResponse format
      return {
        data: { data: responseData, success: true, message: response.statusText } as ApiResponse<T>,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle AbortError (timeout)
      if (error instanceof Error && error.name === 'AbortError') {
        throw createApiError({ message: 'Request timeout', status: 0, code: 'TIMEOUT' });
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw createApiError({ message: 'Network error', status: 0, code: 'NETWORK_ERROR' });
      }

      // Re-throw API errors
      if (isApiError(error)) {
        throw error;
      }

      // Handle unknown errors
      throw createApiError(error);
    }
  }

  // Convenience methods
  async get<T = unknown>(url: string, config: Partial<RequestConfig> = {}) {
    return this.request<T>({ ...config, url, method: 'GET' });
  }

  async post<T = unknown>(url: string, data?: unknown, config: Partial<RequestConfig> = {}) {
    return this.request<T>({ ...config, url, method: 'POST', data });
  }

  async put<T = unknown>(url: string, data?: unknown, config: Partial<RequestConfig> = {}) {
    return this.request<T>({ ...config, url, method: 'PUT', data });
  }

  async delete<T = unknown>(url: string, config: Partial<RequestConfig> = {}) {
    return this.request<T>({ ...config, url, method: 'DELETE' });
  }

  async patch<T = unknown>(url: string, data?: unknown, config: Partial<RequestConfig> = {}) {
    return this.request<T>({ ...config, url, method: 'PATCH', data });
  }
}

/**
 * Create configured HTTP client instance
 */
const createHttpClient = (config: Partial<HttpClientConfig> = {}): HttpClient => {
  return new HttpClient(config);
};

/**
 * Default HTTP client instance
 */
const httpClient = createHttpClient();

/**
 * Check if error is an API error
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    error !== null &&
    typeof error === 'object' &&
    'message' in error &&
    'status' in error &&
    'code' in error
  );
}

/**
 * Create a standardized ApiError from a failed HTTP response.
 * Understands the backend error shape (error, message, status, code)
 * and falls back gracefully when fields are missing.
 */
function createApiErrorFromResponse(responseData: unknown, response: Response): ApiError {
  if (isBackendErrorResponse(responseData)) {
    const message =
      responseData.message || responseData.error || response.statusText || 'Request failed';

    // Backend sends `errorCode`; we normalize it to `code` in ApiError so the rest of the app keeps working using "code".
    const code = typeof responseData.errorCode === 'string' ? responseData.errorCode : 'HTTP_ERROR';

    return {
      message,
      status: response.status,
      code,
      error: typeof responseData.error === 'string' ? responseData.error : undefined,
      path: typeof responseData.path === 'string' ? responseData.path : undefined,
      timestamp: typeof responseData.timestamp === 'string' ? responseData.timestamp : undefined,
    };
  }

  return {
    message: response.statusText || 'Request failed',
    status: response.status,
    code: 'HTTP_ERROR',
  };
}

/**
 * Create standardized API error from various error types
 */
export function createApiError(error: unknown): ApiError {
  // If it's already an API error, return as-is
  if (isApiError(error)) {
    return error;
  }

  // Handle fetch-related errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      message: 'Network error - unable to connect to server',
      status: 0,
      code: 'NETWORK_ERROR',
    };
  }

  // Handle timeout errors
  if (error instanceof Error && error.name === 'AbortError') {
    return { message: 'Request timeout', status: 0, code: 'TIMEOUT' };
  }

  // Handle generic errors
  if (error instanceof Error) {
    return { message: error.message, status: 0, code: 'UNKNOWN_ERROR' };
  }

  // Handle error objects with status/message
  if (error && typeof error === 'object') {
    return {
      message:
        'message' in error && typeof error.message === 'string' ? error.message : 'Request failed',
      status: 'status' in error && typeof error.status === 'number' ? error.status : 0,
      code: 'code' in error && typeof error.code === 'string' ? error.code : 'HTTP_ERROR',
    };
  }

  return { message: 'An unknown error occurred', status: 0, code: 'UNKNOWN_ERROR' };
}

/**
 * Retry mechanism for failed requests
 */

async function retryRequest<T>(
  requestFn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: unknown = new Error('No attempts made');
  const totalAttempts = maxRetries + 1;

  for (let attempt = 1; attempt <= totalAttempts; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;

      // Don't retry on authentication errors (4xx)
      if (isApiError(error) && error.status >= 400 && error.status < 500) {
        throw error;
      }

      // Don't retry on the last attempt
      if (attempt === totalAttempts) {
        break;
      }

      // Exponential backoff
      const waitTime = delay * Math.pow(2, attempt - 1);
      await new Promise((resolve) => setTimeout(resolve, waitTime));
    }
  }

  throw lastError;
}

/**
 * Generic request wrapper with error handling and retry logic.
 */
export async function makeRequest<T>(
  requestConfig: RequestConfig,
  options: { retries?: number; retryDelay?: number } = {}
): Promise<ApiResponse<T>> {
  const { retries = 1, retryDelay = 1000 } = options;

  return retryRequest(
    async () => {
      const response = await httpClient.request<T>(requestConfig);
      return response.data;
    },
    retries,
    retryDelay
  );
}
